import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import './EnvironmentScreen.css';
import DatabaseCard from './DatabaseCard';
import SftGlobalData from '../../library/SftGlobalData';
import Button from '@mui/material/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Refresh } from '@mui/icons-material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import { Box, Tab, Tabs, Tooltip, styled } from '@mui/material';
import leeuw from '../../assets/leeuw.png';
import coq from '../../assets/coq.png';
import PropTypes from 'prop-types';
import Colors from '../../library/Colors';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Modal } from '@mui/material';


const StyledModal = styled(Modal)`
    position: fixed;
    text-align: center;
    z-index: 1300;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    `;

const Backdrop = styled('div')`
    z-index: -1;
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    -webkit-tap-highlight-color: transparent;
    `;

const style = {
    bgcolor: 'background.paper',
    border: '0px solid #000',
    borderRadius: 6,
    p: 2,
    px: 4,
    pb: 3,
};

function TabPanel(props) {
    const { children, value, index, maxHeight, scroll = true, ...other } = props;
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
        style={scroll ? {maxHeight: props.maxHeight-200, overflowY: "auto", overflowX: "hidden"} : {}}
      >
        {value === index && (
          <Box>
            <div style={{paddingTop: 0}}>{children}</div>
          </Box>
        )}
      </div>
    );
}
  
TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};
  
function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const EnvironmentScreen = (props) =>  {
    const [selectedDb, setSelectedDb] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [region, setRegion] = useState('all');      // 'all' | 'VL' | 'WL'
    const [env, setEnv] = useState('all');             // 'all' | 'live' | 'test' | 'local'
    // Initialise view/sort from sessionStorage on the FIRST render so the correct layout paints
    // immediately. Restoring these in a useEffect instead caused a visible flash (compact shown
    // briefly, then switching to the saved 'regular' view) every time the screen (re)mounted.
    const [sort, setSort] = useState(() => {           // 'az' | 'za' | 'num'
        const s = sessionStorage.getItem('env_sort');
        return (s === 'az' || s === 'za' || s === 'num') ? s : 'az';
    });
    const [letter, setLetter] = useState('');          // '' | '#' | 'A'..'Z'
    const [view, setView] = useState(() => {           // 'compact' | 'regular'
        const v = sessionStorage.getItem('env_view');
        return (v === 'compact' || v === 'regular') ? v : 'compact';
    });
    const [inputFinish, setInputFinish] = useState(false);

    const [loading, setLoading] = useState(true);
    const [apiAccounts, setApiAccounts] = useState([]);
    const [apiUsers, setApiUsers] = useState([]);
    const [value, setValue] = useState(0);
    const [apiModal, setApiModal] = useState(false);
    const [selectedAPI, setSelectedAPI] = useState({id: 0, user_stats:[], fullname: ""});
    const [showBackToTop, setShowBackToTop] = useState(false);


    let navigate = useNavigate();
    const inputEl = useRef();
    const scrollContainerRef = useRef(null);

    // Back-to-top: the screen scrolls inside the global ".app-content" container, so we watch its
    // scroll position and reveal the button once the user has scrolled down a bit.
    useEffect(() => {
        const el = document.querySelector('.app-content');
        if (!el) return;
        scrollContainerRef.current = el;
        const onScroll = () => { setShowBackToTop(el.scrollTop > 300); };
        el.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    const scrollToTop = () => {
        const el = scrollContainerRef.current || document.querySelector('.app-content');
        if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    };


    //---------------------------------------------------------------------------------------------
    //    FUNCTIONS
    //---------------------------------------------------------------------------------------------
    // Escape special regex characters in search value
    const escapeRegex = (str) => {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // Search semantics preserved from the original implementation:
    //  - trailing "*" => exact customer-number match (^...$)
    //  - otherwise contains-match across description / alt_name / cust_no / search_string
    //    plus an exact customer-number match.
    const matchesSearch = (db) => {
        const isWildcard = searchValue.substring(searchValue.length - 1) === "*";
        return (
            (!isWildcard && db.description.match(new RegExp(escapeRegex(searchValue), "i"))) ||
            (!isWildcard && db.alt_name.match(new RegExp(escapeRegex(searchValue), "i"))) ||
            (isWildcard && db.cust_no.match(new RegExp("^" + escapeRegex(searchValue.slice(0, -1)) + "$", 'i'))) ||
            (!isWildcard && db.cust_no.match(new RegExp("^" + escapeRegex(searchValue) + "$", 'i'))) ||
            (!isWildcard && db.cust_no.match(new RegExp(escapeRegex(searchValue), "i"))) ||
            (!isWildcard && db.search_string.match(new RegExp(escapeRegex(searchValue), "i")))
        );
    };

    const firstChar = (name) => (name || '').replace(/[^a-z0-9]/gi, '').charAt(0).toUpperCase();

    const passesRegion = (db) => region === 'all' || db.cust_zone === region;
    const passesEnv = (db) => {
        if (env === 'all') return true;
        if (env === 'live') return db.cust_is_test == "0" && db.cust_is_local == "0";
        if (env === 'test') return db.cust_is_test == "1";
        if (env === 'local') return db.cust_is_local == "1";
        return true;
    };
    const passesLetter = (db) => {
        if (!letter) return true;
        const c = firstChar(db.description);
        return letter === '#' ? /[0-9]/.test(c) : c === letter;
    };

    const passesFilters = (db) => matchesSearch(db) && passesRegion(db) && passesEnv(db) && passesLetter(db);

    const sortDbs = (a, b) => {
        if (sort === 'num') return (parseInt(a.cust_no) || 99999) - (parseInt(b.cust_no) || 99999);
        const cmp = a.description.toLowerCase().localeCompare(b.description.toLowerCase());
        return sort === 'za' ? -cmp : cmp;
    };

    const filteredDbs = props.dbs.filter(passesFilters).sort(sortDbs);

    const getDbs = filteredDbs.map((data) => (
        <DatabaseCard
            key={data.id}
            data={data}
            view={view}
            theme={props.theme}
            t={props.t}
            func={() => { if (SftGlobalData.debug) { console.log('environment pressed ' + data.description); console.log(data); } setSelectedDb(data); }}
        />
    ));

    useEffect(() => {
        if(props.dbs.length === 0){
            props.changeScreen('login');
            navigate("/"+props.lang+"/login");
        } else {
            if (selectedDb !== ""){
                sessionStorage.removeItem('key');
                sessionStorage.setItem('key', selectedDb.encrypted_key);
                
                props.selectDb(selectedDb, selectedDb.encrypted_key);
                props.changeScreen('home');
                return props.sftStatus != "0" ? navigate("/"+props.lang+"/licence") : navigate("/"+props.lang+"/home");
            } else {
                if(SftGlobalData.debug){
                    console.log('dbs length', props.dbs.length);
                }
                if(props.dbs.length === 1){
                    if(!props.noAutoRedirect){
                        setSelectedDb(props.dbs[0]);
                        props.selectDb(props.dbs[0], props.dbs[0].encrypted_key);
        
                        sessionStorage.removeItem('key');
                        sessionStorage.setItem('key', props.dbs[0].encrypted_key);
        
                        props.changeScreen('home');
                        props.sftStatus != "0" ? navigate("/"+props.lang+"/licence") : navigate("/"+props.lang+"/home");
                    } else {
                        if(props.dbs.length < 15){
                            setSearchValue(props.dbs[0].cust_no + "*");
                        }
                        props.changeScreen('environment');
                    }
                } else{
                    props.changeScreen('environment');
                    // view/sort are restored lazily in their useState initialisers (above) to avoid
                    // a flash on (re)mount; only the search term is restored here.
                    let sessionSearchEnv = sessionStorage.getItem('search');
                    if(sessionSearchEnv !== null){setSearchValue(sessionSearchEnv);}
                    try{inputEl.current.select();}catch(e){}
                    if(props.group === "DEV" || props.group === "ADMIN" || props.group === "SUPER"){
                        loadApi();
                    }
                }
            }
        }
        
     },[selectedDb]);

     const loadApi = () => {
        // API users/accounts come through auth: the admin API token stays server-side.
        SftGlobalData.post('apiAdmin&action=fetch', { sft_user_id: props.sftUserId })
            .then(response => {
                if(SftGlobalData.debug){
                    console.log('response apiAdmin fetch', response);
                }
                if(response?.success){
                    setApiUsers(response.data?.users ?? []);
                    setApiAccounts(response.data?.accounts ?? []);
                }
                setLoading(false);
            });
     }


     useEffect(()=>{
        if(!inputFinish){
            if(searchValue.length > 1){
                inputEl.current.select();
            }
            if(searchValue.length !== 0){
                setInputFinish(true);
            }
        }
        
     }, [searchValue]);


    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
          let newSelection = props.dbs.filter(passesFilters);
          if(newSelection.length === 1){
                SftGlobalData.easter2 = false;
                setSelectedDb(newSelection[0]);
            } else if(searchValue.toLowerCase() === "vibrator"){
                SftGlobalData.easter2 = true;
            } else {
                SftGlobalData.easter3 = false;
                SftGlobalData.easter2 = false;
                SftGlobalData.easter1 = false;
            }
        }
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const deleteAccount = (id) => {
        SftGlobalData.post('apiAdmin&action=deleteAccount', { sft_user_id: props.sftUserId, id: id })
            .then(response => {
                if(SftGlobalData.debug){
                    console.log('response apiAdmin deleteAccount', response);
                }
                if(!response?.success){
                    props.notify(response?.message, 7500, "top-right", props.theme, 1);
                }
                setLoading(true);
                loadApi();
            });
    }

    const deleteUser = (id) => {
        SftGlobalData.post('apiAdmin&action=deleteUser', { sft_user_id: props.sftUserId, id: id })
            .then(response => {
                if(SftGlobalData.debug){
                    console.log('response apiAdmin deleteUser', response);
                }
                if(!response?.success){
                    props.notify(response?.message, 7500, "top-right", props.theme, 1);
                }
                setLoading(true);
                loadApi();
            });
    }

    const removeAccount = (user, accountId) => {
        let postData = { sft_user_id: props.sftUserId, account_id: accountId, email: user };
        SftGlobalData.post('apiAdmin&action=removeAccountUser', postData)
        .then(response => {
            if(SftGlobalData.debug){
                console.log("postData", postData);
                console.log('response', response);
            }
            if(response?.success){
                props.notify(props.t('savedChanges'), 7500, "top-right", props.theme, 1); 
                removeAccountFromList(accountId);
            } else {
                props.notify(response?.message, 7500, "top-right", props.theme, 1); 
            }
        }).catch(error => {
            props.notify(error?.message, 7500, "top-right", props.theme, 1); 
        });
    }

    const removeAccountFromList = (accountId) => {
        const updatedStats = selectedAPI.user_stats.filter(account => account.account_id !== accountId);
        setSelectedAPI(prevState => ({
          ...prevState,
          user_stats: updatedStats
        }));
      };

    const editUser = (row) => {
        setSelectedAPI(row);
        setApiModal(true);
    }

    useEffect(()=>{
        if(!apiModal && selectedAPI.id !== 0){
            setLoading(true);
            loadApi();
        }
    }, [apiModal])

    //---------------------------------------------------------------------------------------------
    //    OUTPUT
    //---------------------------------------------------------------------------------------------
    return (
        <div className={"environment " + (searchValue === "stefan" ? "stefan" : "")}>
            {showBackToTop && <button
                type="button"
                className="environment__back_to_top"
                title={props.t('backToTop') || 'Back to top'}
                aria-label="Back to top"
                onClick={scrollToTop}
            ><KeyboardArrowUpIcon /></button>}
            <StyledModal
                aria-labelledby="unstyled-modal-title"
                aria-describedby="unstyled-modal-description"
                open={apiModal}
                onClose={()=>{setApiModal(false)}}
                BackdropComponent={Backdrop}
                >
                <Box sx={style}  style={{minWidth: 750, minHeight: 500, width: "90%"}} className={props.theme + "  box_fade-in"}>
                    <h2>API user edit ({selectedAPI?.uses_accounts ? selectedAPI?.fullname : selectedAPI?.email})</h2>
                    <hr/>
                    <div className={'environment__card_body_container '}>
                        {/* MUI Datagrid with accounts */}
                        <div className='rowContainerCenter _100perc' style={{maxHeight: 500}}>
                            <DataGridPro
                                  //apiRef={apiRef}
                                  //initialState={initialGrid}
                                  className={'excel_grid ' + props.theme}
                                  style={{width: "100%"}}
                                  columns={[
                                    {
                                        field: 'id',
                                        headerName: 'Account ID',
                                        minWidth: 100,
                                        maxWidth: 100,
                                        width: 100,
                                        align: "center",
                                    },
                                    {
                                        field: 'name',
                                        headerName: "Name",
                                        minWidth: 200,
                                        align: "left",
                                    },
                                    {
                                        field: 'last_call',
                                        headerName: "Last call date",
                                        minWidth: 200,
                                        align: "left",
                                    },
                                    {
                                        field: 'calls',
                                        headerName: "# calls",
                                        minWidth: 100,
                                        align: "center",
                                        headerAlign: "center"
                                    },
                                    {
                                        field: 'actions',
                                        type: 'actions',
                                        width: 40,
                                        maxWidth: 40,
                                        minWidth: 40,
                                        getActions: (params) => [
                                           <GridActionsCellItem
                                            key={`remove-account-${params.row.id}`}
                                            icon={<DeleteIcon  sx={{ color: Colors.def_red }} />}
                                            label="Remove"
                                            onClick={()=>{removeAccount(selectedAPI.email, params.row.id);}}
                                          />
                                        ],
                                    },
                                  ]}
                                  rows={selectedAPI.user_stats.map((row, index) => {return {
                                    id: row.account_id,
                                    name: row.name,
                                    calls: row?.calls ?? "0",
                                    last_call: row?.last_call ?? "0000-00-00 00:00:00",
                                    actions: "",
                                    }                          
                                })} 
                                  //onColumnWidthChange={()=>{visibilityChangedHandler();}}
                                  //onColumnVisibilityModelChange={()=>{visibilityChangedHandler();}}
                                  //onPreferencePanelClose={()=>{visibilityChangedHandler();}}
                                  rowThreshold={0}
                                  rowHeight={38}
                                  loading={loading}
                                  pagination={true}
                            /> 
                      </div>
                    </div>
                    
                </Box>
            </StyledModal>
            <Box className="environment__card_header_margin environment__search_width" style={props.todo ? {width: "75%"} : {}} >
                <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, backgroundColor: props.theme === 'dark' ? Colors.dark_basic : Colors.light_basic }} className={' _100perc'}>
                    {(props.group === "DEV" || props.group === "ADMIN" || props.group === "SUPER")
                        ? (<Tabs value={value} onChange={handleChange} className={props.theme} variant="scrollable" scrollButtons="auto" sx={{ flex: 1, minWidth: 0 }}>
                            <Tab label={"DATABASES "} {...a11yProps(0)} className={props.theme}/>
                            <Tab label={"API ACCOUNTS "} {...a11yProps(1)} className={props.theme}/>
                            <Tab label={"API USERS "} {...a11yProps(2)} className={props.theme}/>
                        </Tabs>)
                        : <Box sx={{ flex: 1 }} />}
                    {/* Refresh + new-database actions, pinned to the right of the tab bar. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pr: 1 }}>
                        <Tooltip title="Refresh" arrow><Button className={"environment__refresh_btn " + (searchValue === "vibrator" ? "shaker " : "")} onClick={()=>{props.onRefresh(); setLoading(true); loadApi();}}><Refresh /></Button></Tooltip>
                        {(props.group === "ADMIN" || props.group === "SUPER") && props.screenWidth > 900 && <Button className="environment__add_btn" onClick={()=>{if(searchValue !== "") {navigate("/"+props.lang+"/environment/"+searchValue);} else { navigate("/"+props.lang+"/environment/+");}}}>+</Button>}
                    </Box>
                </Box>
                {/* Search + filters sit below the tab bar. Search is shared across all tabs (it
                    filters the databases list AND the API grids); the region/env/sort controls
                    apply to the DATABASES tab only. */}
                {(props.dbs.length > 2 || props.sftStatus != "0") && <div className={props.theme + " environment__toolbar"} >
                    <div className="environment__search_wrap">
                        <span className="environment__search_icon"><FontAwesomeIcon icon={faSearch} /></span>
                        <input
                            ref={inputEl}
                            type="text"
                            name="search"
                            className={props.theme+"_accent environment__search_input " + (searchValue === "vibrator" ? "shaker" : "")}
                            placeholder={props.t('search')}
                            value={searchValue}
                            autoFocus
                            onChange={e => {const v = e.target.value.replace("+", "").replace("\\", ""); setSearchValue(v); sessionStorage.removeItem('search'); sessionStorage.setItem('search', v);}}
                            onKeyDown={handleKeyDown}
                            onFocus={(event) => event.target.select()}
                        />
                        {searchValue !== "" && <button
                            type="button"
                            className="environment__search_clear"
                            title={props.t('clearFilters')}
                            aria-label="Clear search"
                            onClick={()=>{setSearchValue(''); sessionStorage.removeItem('search'); sessionStorage.setItem('search', ''); try{inputEl.current.focus();}catch(e){}}}
                        ><FontAwesomeIcon icon={faTimes} /></button>}
                    </div>
                    {value === 0 && <>
                        <div className="environment__chip_group" title="Regio">
                            <button className={"environment__chip " + (region === 'all' ? "active" : "")} onClick={()=>setRegion('all')}>{props.t('all')}</button>
                            <Tooltip title="VLAANDEREN" arrow><button className={"environment__chip environment__chip_icon " + (region === 'VL' ? "active" : "")} onClick={()=>setRegion(region === 'VL' ? 'all' : 'VL')}><img alt="Vlaanderen" src={leeuw} /></button></Tooltip>
                            <Tooltip title="WALLONIE" arrow><button className={"environment__chip environment__chip_icon " + (region === 'WL' ? "active" : "")} onClick={()=>setRegion(region === 'WL' ? 'all' : 'WL')}><img alt="Wallonie" src={coq} /></button></Tooltip>
                        </div>
                        <div className="environment__toolbar_divider"></div>
                        <div className="environment__chip_group" title="Omgeving">
                            <button className={"environment__chip " + (env === 'all' ? "active" : "")} onClick={()=>setEnv('all')}>{props.t('all')}</button>
                            <button className={"environment__chip " + (env === 'live' ? "active" : "")} onClick={()=>setEnv(env === 'live' ? 'all' : 'live')}><span className="environment__dot environment__env_live"></span>{props.t('live')}</button>
                            <button className={"environment__chip " + (env === 'test' ? "active" : "")} onClick={()=>setEnv(env === 'test' ? 'all' : 'test')}><span className="environment__dot environment__env_test"></span>{props.t('training')}</button>
                            <button className={"environment__chip " + (env === 'local' ? "active" : "")} onClick={()=>setEnv(env === 'local' ? 'all' : 'local')}><span className="environment__dot environment__env_local"></span>{props.t('lokaal')}</button>
                        </div>
                        <select className={props.theme+"_accent environment__sort_select"} value={sort} onChange={e => {setSort(e.target.value); sessionStorage.setItem('env_sort', e.target.value);}}>
                            <option value="az">{props.t('sortNameAsc')}</option>
                            <option value="za">{props.t('sortNameDesc')}</option>
                            <option value="num">{props.t('sortCustNo')}</option>
                        </select>
                    </>}
                </div>}
                <TabPanel value={value} index={0} maxHeight={props.maxHeight} scroll={false}>
                    <div className={props.theme + " environment__count_row"}>
                        <div className="environment__count_left">
                            <span className="environment__count_label"><b>{filteredDbs.length}</b> {props.t('databaseCount', {total: props.dbs.length})}</span>
                        </div>
                        <div className="environment__count_right">
                            <span className="environment__weergave_label">{props.t('weergave')}</span>
                            <div className="environment__view_toggle">
                                <button className={"environment__view_btn " + (view === 'compact' ? "active" : "")} onClick={()=>{setView('compact'); sessionStorage.setItem('env_view','compact');}} title={props.t('compact')}><ViewListIcon fontSize="small" /></button>
                                <button className={"environment__view_btn " + (view === 'regular' ? "active" : "")} onClick={()=>{setView('regular'); sessionStorage.setItem('env_view','regular');}} title={props.t('regular')}><GridViewIcon fontSize="small" /></button>
                            </div>
                            <button className="environment__clear_btn" onClick={()=>{setSearchValue(''); setRegion('all'); setEnv('all'); setLetter(''); sessionStorage.removeItem('search'); sessionStorage.setItem('search','');}}>{props.t('clearFilters')}</button>
                        </div>
                    </div>
                    {(props.dbs.length >= 1) && <div className="environment__content">
                        <div className={"environment__grid environment__grid_" + view}>{getDbs}</div>
                        <div className={props.theme + " environment__az_rail"}>
                            {['#'].concat('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')).map((L) => (
                                <button key={L} className={"environment__az_btn " + (letter === L ? "active" : "")} onClick={()=>setLetter(letter === L ? '' : L)}>{L}</button>
                            ))}
                        </div>
                    </div>}
                </TabPanel>
                <TabPanel value={value} index={1} maxHeight={props.maxHeight}>
                    <div className={'environment__card_body_container '}>
                        {/* MUI Datagrid with accounts */}
                        <div className='rowContainerCenter _100perc' style={{maxHeight: props.maxHeight-300}}>
                            <DataGridPro
                                  //apiRef={apiRef}
                                  //initialState={initialGrid}
                                  className={'excel_grid ' + props.theme}
                                  style={{minHeight: 500, width: "100%"}}
                                  columns={[
                                    {
                                        field: 'id',
                                        headerName: 'Account ID',
                                        minWidth: 100,
                                        maxWidth: 100,
                                        width: 100,
                                        align: "center",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'name',
                                        headerName: "Name",
                                        minWidth: 200,
                                        align: "left",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'users',
                                        headerName: 'Users',
                                        minWidth: 60,
                                        maxWidth: 60,
                                        width: 60,
                                        align: "center",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'active',
                                        headerName: 'Active?',
                                        minWidth: 60,
                                        align: 'center',
                                        display: 'flex',      
                                        renderCell: (params) => {
                                            if(params.row.active){
                                                return <FontAwesomeIcon size={"2x"} icon={faCheck} color={Colors.def_green} />
                                            } else {
                                                return <FontAwesomeIcon size={"2x"} icon={faTimes} color={Colors.def_red} />
                                            }
                                        }
                                    },
                                    {
                                        field: 'actions',
                                        type: 'actions',
                                        width: 40,
                                        maxWidth: 40,
                                        minWidth: 40,
                                        display: 'flex',      
                                        getActions: (params) => params.row.users > 0 ? [] : [
                                            <GridActionsCellItem
                                                key={`delete-account-${params.row.id}`}
                                                icon={<DeleteIcon  sx={{ color: Colors.def_red }} />}
                                                label="Remove"
                                                onClick={()=>{deleteAccount(params.row.id);}}
                                            />
                                        ],
                                    },
                                  ]}
                                  rows={apiAccounts.filter(row => (row.name.match(new RegExp(escapeRegex(searchValue), "i")) || row.id == searchValue )).map((row, index) => {return {
                                          id: row.id,
                                          name: row.name,
                                          active: (row.active && row.has_gates),
                                          users: row.users_count,
                                          actions: "",
                                      }                          
                                  })} 
                                  //onColumnWidthChange={()=>{visibilityChangedHandler();}}
                                  //onColumnVisibilityModelChange={()=>{visibilityChangedHandler();}}
                                  //onPreferencePanelClose={()=>{visibilityChangedHandler();}}
                                  rowThreshold={0}
                                  rowHeight={38}
                                  loading={loading}
                            /> 
                      </div>
                    </div>
                </TabPanel>
                <TabPanel value={value} index={2} maxHeight={props.maxHeight}>
                    <div className={'environment__card_body_container '}>
                        {/* MUI Datagrid with users */}
                        <div className='rowContainerCenter _100perc' style={{maxHeight: props.maxHeight-300}}>
                            <DataGridPro
                                  //apiRef={apiRef}
                                  //initialState={initialGrid}
                                  className={'excel_grid ' + props.theme}
                                  style={{minHeight: 500, width: "100%"}}
                                  columns={[
                                    {
                                        field: 'avatar',
                                        headerName: '',
                                        width: 45,
                                        maxWidth: 45,
                                        minWidth: 45,
                                        align: 'center',
                                        display: 'flex',      
                                        renderCell: (params) => {
                                            if(params.row.avatar !== null)
                                                return <img alt="Avatar" style={{scale: 'fitXY', width: 38, height: 38, borderWidth: 0}} src={params.row.avatar} />
                                            else
                                                return <div></div>
                                        }
                                    },
                                    {
                                        field: 'id',
                                        headerName: 'ID',
                                        minWidth: 60,
                                        maxWidth: 60,
                                        width: 60,
                                        align: "center",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'fullname',
                                        headerName: "Name",
                                        minWidth: 200,
                                        align: "left",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'firstname',
                                        headerName: "First name",
                                        minWidth: 100,
                                        align: "left",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'lastname',
                                        headerName: "Last name",
                                        minWidth: 100,
                                        align: "left",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'email',
                                        headerName: 'Email',
                                        minWidth: 200,
                                        width: 230,
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'is_admin',
                                        headerName: 'Admin?',
                                        minWidth: 60,
                                        align: 'center',
                                        display: 'flex',      
                                        renderCell: (params) => {
                                            if(params.row.is_admin){
                                                return <FontAwesomeIcon size={"2x"} icon={faCheck} color={Colors.def_green} />
                                            } else {
                                                return <FontAwesomeIcon size={"2x"} icon={faTimes} color={Colors.def_red} />
                                            }
                                        }
                                    },
                                    {
                                        field: 'accounts_length',
                                        headerName: '# accounts',
                                        minWidth: 100,
                                        width: 100,
                                        align: "center",
                                        headerAlign: "center",
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'accounts',
                                        headerName: 'Accounts',
                                        minWidth: 200,
                                        width: 230,
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'total_calls',
                                        headerName: '# calls',
                                        align: "center",
                                        headerAlign: "center",
                                        minWidth: 120,
                                        width: 120,
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'last_call',
                                        headerName: 'Last call',
                                        minWidth: 175,
                                        width: 175,
                                        display: 'flex',      
                                    },
                                    {
                                        field: 'actions2',
                                        type: 'actions',
                                        width: 40,
                                        maxWidth: 40,
                                        minWidth: 40,
                                        display: 'flex',      
                                        getActions: (params) => [
                                            <GridActionsCellItem
                                            key={`edit-user-${params.row.id}`}
                                            icon={<EditIcon  sx={{ color: Colors.def_orange }} />}
                                            label="Edit"
                                            onClick={()=>{editUser(params.row);}}
                                          />
                                        ],
                                    },
                                    {
                                        field: 'actions',
                                        type: 'actions',
                                        width: 40,
                                        maxWidth: 40,
                                        minWidth: 40,
                                        display: 'flex',      
                                        getActions: (params) => params.row.accounts_length > 0 ? [] : [
                                            <GridActionsCellItem
                                                key={`delete-user-${params.row.id}`}
                                                icon={<DeleteIcon  sx={{ color: Colors.def_red }} />}
                                                label="Remove"
                                                onClick={()=>{deleteUser(params.row.id);}}
                                            />
                                        ],
                                    }, 
                                    
                                  ]}
                                  rows={apiUsers.filter(row => (
                                                (typeof row?.accounts !== "undefined" ? row.fullname?.match(new RegExp(escapeRegex(searchValue), "i")) : (row.firstname?.match(new RegExp(escapeRegex(searchValue), "i")) || row.lastname?.match(new RegExp(escapeRegex(searchValue), "i")))) || 
                                                row.email?.match(new RegExp(escapeRegex(searchValue), "i")) || 
                                                (typeof row?.accounts !== "undefined" ? row?.accounts?.some(item => item?.name?.match(new RegExp(escapeRegex(searchValue), "i"))) : row?.stats.some(item => item?.name?.match(new RegExp(escapeRegex(searchValue), "i")))) || 
                                                row.id == searchValue )).map((row, index) => {
                                        return {
                                          id: row.id,
                                          fullname: (row.lastname + " " + row.firstname),
                                          firstname: row.firstname,
                                          lastname: row.lastname,
                                          email: row.email,
                                          avatar: row.avatar,
                                          is_admin: row.is_admin,   
                                          total_calls: row?.total_calls,
                                          last_call: row?.last_call,
                                          accounts: row?.accounts?.map(item => item.name).join(', '),
                                          accounts_length: row?.accounts?.length,
                                          uses_accounts:  typeof row?.accounts !== "undefined",
                                          user_stats: (typeof row?.stats !== "undefined" && row?.stats) ?row?.stats : [],
                                      }                          
                                  })} 
                                  //onColumnWidthChange={()=>{visibilityChangedHandler();}}
                                  //onColumnVisibilityModelChange={()=>{visibilityChangedHandler();}}
                                  //onPreferencePanelClose={()=>{visibilityChangedHandler();}}
                                  rowThreshold={0}
                                  rowHeight={38}
                                  loading={loading}
                                  initialState={{
                                    columns: {
                                      columnVisibilityModel: {
                                        // Hide columns firstname and lastname, the other columns will remain visible
                                        firstname: false,
                                        lastname: false,
                                      },
                                    },
                                  }}
                                  
                            /> 
                      </div>
                    </div>
                </TabPanel>
            </Box>
            {/*(props.dbs.length === 1) && <div className="message">{props.t('redirect_message')}</div>*/}
            {/*(props.dbs.length === 0) && notify(props.t('no_databases'), 1)*/}
        </div>
        
    );
}

export default EnvironmentScreen;
